/**
 * Typed API client.
 *
 * One place that knows how to talk to the server: how the token is carried,
 * how errors are shaped, and what each endpoint returns. Components never call
 * `fetch` directly, so error handling and auth cannot drift between screens.
 */

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

/** An error carrying the server's stable machine-readable code. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** The wallet is locked and the UI should prompt for the vault password. */
  get isLocked(): boolean {
    return this.code === 'VAULT_LOCKED';
  }

  get isAuth(): boolean {
    return this.status === 401 && this.code !== 'VAULT_LOCKED';
  }
}

const TOKEN_KEY = 'gauntlet.token';

/**
 * The session token lives in `localStorage`.
 *
 * That is a real trade-off worth being honest about: `localStorage` is readable
 * by any script running on the page, so an XSS becomes a session compromise.
 * The mitigations are that the server ships a strict CSP with no third-party
 * script origins, the app has no user-generated HTML, and — critically —
 * *holding a session token does not let anyone spend*, because spending
 * additionally requires the vault password, which is never stored anywhere.
 */
export const tokenStore = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* Private browsing with storage disabled — the session lives in memory. */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

let onUnauthorized: (() => void) | undefined;

/** Register a callback for when the server rejects our session. */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const token = tokenStore.get();

  const response = await fetch(`/api${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });

  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const payload = parsed as Partial<ApiErrorBody>;
    const code = payload?.error?.code ?? 'UNKNOWN';
    const message = payload?.error?.message ?? `Request failed (${response.status})`;

    // A dead session should bounce to login once, from here, rather than every
    // screen having to notice independently.
    if (response.status === 401 && code !== 'VAULT_LOCKED' && code !== 'BAD_PASSWORD') {
      tokenStore.clear();
      onUnauthorized?.();
    }

    throw new ApiError(response.status, code, message, payload?.error?.details);
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

export interface ChainSummary {
  id: string;
  name: string;
  symbol: string;
  family: string;
  decimals: number;
  slip44: number;
  testnet: boolean;
  tier: 'full' | 'watch' | 'derive';
  coingeckoId: string | null;
  explorer: { tx: string; address: string } | null;
  chainId?: number;
  l2?: boolean;
}

export interface RegistryStats {
  total: number;
  mainnet: number;
  testnet: number;
  byFamily: Record<string, number>;
  byTier: Record<string, number>;
  tokens: number;
}

export interface Health {
  status: string;
  version: string;
  chains: RegistryStats;
  setupComplete: boolean;
  uptimeSec: number;
}

export interface Account {
  id: number;
  wallet_id: number;
  chain_id: string;
  account_index: number;
  address_type: string | null;
  xpub: string | null;
  derivation_path: string;
  label: string | null;
  next_receive: number;
}

export interface Wallet {
  id: number;
  name: string;
  type: 'hd' | 'watch' | 'hardware';
  fingerprint: string | null;
  has_passphrase: number;
  archived: number;
  created_at: number;
  unlocked: boolean;
  lockRemainingSec: number | null;
  accounts: Account[];
}

export interface AddressRecord {
  id: number;
  account_id: number;
  chain_id: string;
  address: string;
  derivation_path: string;
  change: number;
  address_index: number;
  used: number;
  label: string | null;
}

export interface Holding {
  assetId: number;
  chainId: string;
  token: string | null;
  symbol: string;
  name: string;
  decimals: number;
  amount: string;
  amountFormatted: string;
  value: string;
  price: string | null;
  change24h: number | null;
  allocation: number;
  stale: boolean;
}

export interface Portfolio {
  currency: string;
  totalValue: string;
  change24hValue: string;
  change24hPercent: number;
  holdings: Holding[];
  assetCount: number;
  chainCount: number;
  updatedAt: number;
}

export interface FeeEstimate {
  fastest: number;
  fast: number;
  medium: number;
  slow: number;
  unit: string;
  eip1559?: {
    baseFee: string;
    priorityFast: string;
    priorityMedium: string;
    prioritySlow: string;
  };
}

export interface BuiltTx {
  chainId: string;
  payload: string;
  format: 'psbt' | 'rlp' | 'json' | 'raw';
  fee: string;
  totalOut: string;
  changeAmount?: string;
  estimatedSize?: number;
  warnings?: string[];
  inputs?: { txid: string; vout: number; value: string; path?: string }[];
  outputs: { address: string; amount: string }[];
}

export interface Utxo {
  id: number;
  txid: string;
  vout: number;
  value: string;
  scriptPubKey: string;
  confirmations: number;
  address: string;
  path: string;
  frozen: number;
  label: string | null;
}

export type ProfileRole = "guardian" | "teen" | "child" | "viewer";

export interface Profile {
  id: number;
  household_id: number;
  account_index: number;
  name: string;
  role: ProfileRole;
  birth_year: number | null;
  age: number | null;
  avatar: string | null;
  color: string | null;
  can_send: number;
  can_receive: number;
  can_use_card: number;
  per_tx_limit_cents: string | null;
  daily_limit_cents: string | null;
  weekly_limit_cents: string | null;
  approval_over_cents: string | null;
  locked: number;
  has_pin: boolean;
  spentThisWeek?: string;
}

export interface HouseholdOverview {
  profiles: Profile[];
  pendingApprovals: number;
  allowances: number;
}

export interface ApprovalRequest {
  id: number;
  profile_id: number;
  profile_name?: string;
  kind: string;
  payload: string;
  amount_cents: string | null;
  reason: string | null;
  status: string;
  expires_at: number;
  created_at: number;
}

export interface Allowance {
  id: number;
  profile_id: number;
  profile_name: string;
  amount_cents: string;
  frequency: string;
  next_run: number;
  enabled: number;
}

export interface SavingsGoal {
  id: number;
  name: string;
  emoji: string | null;
  target_cents: string;
  saved_cents: string;
  achieved_at: number | null;
}

export interface CardSummary {
  spentToday: string;
  spentMonth: string;
  roundedUp: string;
  declines: number;
  transactions: number;
}

export interface CardRecord {
  id: number;
  profile_id: number;
  nickname: string;
  last4: string | null;
  brand: string | null;
  exp_month: number | null;
  exp_year: number | null;
  currency: string;
  status: "active" | "frozen" | "cancelled";
  per_tx_limit_cents: string | null;
  daily_limit_cents: string | null;
  blocked_categories: string | null;
  round_up_enabled: number;
  summary?: CardSummary;
}

export interface CardTransaction {
  id: number;
  merchant_name: string;
  merchant_category: string | null;
  amount_cents: string;
  currency: string;
  status: string;
  decline_reason: string | null;
  round_up_cents: string | null;
  authorized_at: number;
}

export interface AlertRecord {
  id: number;
  type:
    | "price_above"
    | "price_below"
    | "price_change"
    | "incoming_tx"
    | "outgoing_tx"
    | "balance_below";
  asset_id: number | null;
  chain_id: string | null;
  threshold: string | null;
  currency: string | null;
  note: string | null;
  enabled: number;
  one_shot: number;
  triggered_at: number | null;
  last_value: string | null;
  created_at: number;
}

export interface KnownAddress {
  id: number;
  chain_id: string;
  address: string;
  label: string | null;
  origin: string;
  verified: number;
  times_used: number;
  first_seen: number;
  last_seen: number;
}

export interface AuditEntry {
  action: string;
  detail: string | null;
  ip_address: string | null;
  success: number;
  created_at: number;
}

export interface SessionRecord {
  id: string;
  created_at: number;
  last_seen: number;
  ip_address: string | null;
  user_agent: string | null;
}

export interface OnrampOption {
  id: string;
  name: string;
  kind: "hosted" | "p2p" | "voucher" | "swap";
  summary: string;
  kyc: "required" | "none" | "varies";
  paymentMethods: string[];
  regions: string;
  url: string;
  unavailableReason?: string;
  caveat?: string;
}

export interface CatalogProduct {
  slug: string;
  name: string;
  baseName?: string;
  priceRange?: string;
  currency?: string;
  countryCode?: string;
  categories?: string[];
  iconBackground?: string;
  iconForeground?: string;
  logoPreview?: string;
  iconPreview?: string;
  logoUrl?: string;
  rating?: number;
  reviews?: number;
}

export interface CatalogPage {
  available: boolean;
  products: CatalogProduct[];
  totalCount: number;
  categories: { slug: string; name: string; group: string }[];
  countries: { code: string; name: string }[];
  reason?: string;
}

export interface ProductDetail {
  available: boolean;
  slug: string;
  name?: string;
  currency?: string;
  packages: { value: string; amount: number; usdPrice?: number; eurPrice?: number }[];
  terms?: string;
  termsLink?: string;
  redemptionMethods?: string[];
  outOfStock?: boolean;
  logoUrl?: string;
  reason?: string;
}

export interface SpendOrderRecord {
  id: number;
  product_type: string;
  product_name: string;
  fiat_amount_cents: string;
  currency: string;
  status: string;
  created_at: number;
  profile_name?: string;
}

export interface InheritancePlan {
  id: number;
  heir_name: string;
  status: string;
  check_in_days: number;
  grace_days: number;
  last_check_in: number;
  daysUntilGrace: number;
  daysUntilRelease: number;
  envelope_hint: string | null;
}

export interface TimeLock {
  id: number;
  name: string;
  chain_id: string;
  kind: string;
  unlock_at: number;
  unlocked: boolean;
  daysLeft: number;
}

export interface AddressValidation {
  valid?: boolean;
  reason?: string;
  addressType?: string;
  alsoValidOn?: string[];
  candidates?: string[];
  chainId?: string;
}

export type RequestMode = 'single' | 'multi';
export type RequestStatusValue =
  | 'open'
  | 'paid'
  | 'completed'
  | 'expired'
  | 'cancelled';

export interface PaymentRequest {
  id: number;
  walletId: number;
  accountId: number;
  slug: string;
  mode: RequestMode;
  title: string;
  note: string;
  chainId: string;
  amount: string;
  address: string;
  status: RequestStatusValue;
  webhookUrl: string | null;
  expiresAt: number | null;
  createdAt: number;
}

export interface RequestStatus {
  status: RequestStatusValue;
  confirmedTotal: string;
  pendingTotal: string;
  payments: {
    txid: string;
    amount: string;
    status: string;
    confirmations: number;
    timestamp: number;
  }[];
}

export interface PublicRequest {
  title: string;
  note: string;
  mode: RequestMode;
  amount: string;
  chainId: string;
  symbol: string;
  address: string;
  uri: string;
  status: RequestStatusValue;
  confirmedTotal: string;
  pendingTotal: string;
  payments: {
    txid: string;
    amount: string;
    status: string;
    confirmations: number;
    timestamp: number;
  }[];
  createdAt: number;
  expiresAt: number | null;
}

export interface ApiTokenRow {
  id: number;
  label: string;
  prefix: string;
  revoked: number;
  last_used_at: number | null;
  created_at: number;
}

/** Server rows use snake_case; the UI reads camelCase. */
function mapPaymentRequest(row: Record<string, unknown>): PaymentRequest {
  return {
    id: Number(row.id),
    walletId: Number(row.wallet_id),
    accountId: Number(row.account_id),
    slug: String(row.slug),
    mode: row.mode as RequestMode,
    title: String(row.title),
    note: String(row.note ?? ''),
    chainId: String(row.chain_id),
    amount: String(row.amount),
    address: String(row.address),
    status: row.status as RequestStatusValue,
    webhookUrl: (row.webhook_url as string | null) ?? null,
    expiresAt: (row.expires_at as number | null) ?? null,
    createdAt: Number(row.created_at),
  };
}

export interface SyncResult {
  addressesChecked: number;
  balancesUpdated: number;
  utxosFound: number;
  transfersFound: number;
  errors: { chainId: string; message: string }[];
  durationMs: number;
  /** True when a sync was already running — the request is a no-op, not a failure. */
  skipped?: boolean;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const gauntlet = {
  health: () => api.get<Health>('/health'),
  authStatus: () => api.get<{ setupComplete: boolean; registrationOpen: boolean }>('/auth/status'),

  setup: (username: string, password: string) =>
    api.post<{ user: unknown; token: string }>('/auth/setup', { username, password }),

  login: (username: string, password: string, totp?: string) =>
    api.post<{ user: unknown; token: string }>('/auth/login', { username, password, totp }),

  logout: () => api.post<{ ok: boolean }>('/auth/logout'),
  me: () =>
    api.get<{
      user: { id: number; username: string; totp_enabled: number };
      /** Decided by the server from GAUNTLET_ADMIN_USER; never sent by the client. */
      isAdmin: boolean;
      unlockedWallets: number[];
    }>('/auth/me'),

  chains: (query = '') => api.get<{ chains: ChainSummary[]; stats: RegistryStats }>(`/chains${query}`),

  wallets: () => api.get<{ wallets: Wallet[] }>('/wallets'),
  createWallet: (body: {
    name: string;
    password: string;
    words?: number;
    passphrase?: string;
    mnemonic?: string;
    chains?: string[];
  }) => api.post<{ wallet: Wallet; mnemonic: string; warning: string }>('/wallets', body),

  unlockWallet: (id: number, password: string) =>
    api.post<{ unlocked: boolean; lockRemainingSec: number }>(`/wallets/${id}/unlock`, { password }),
  lockWallet: (id: number) => api.post<{ unlocked: boolean }>(`/wallets/${id}/lock`),
  deleteWallet: (id: number, password: string) =>
    api.del<{ ok: true }>(`/wallets/${id}`, { password }),
  exportWallet: (id: number, password: string) =>
    api.post<{ mnemonic: string; passphrase?: string }>(`/wallets/${id}/export`, { password }),
  addAccount: (id: number, chainId: string) =>
    api.post<{ account: Account }>(`/wallets/${id}/accounts`, { chainId }),
  walletChains: (id: number) =>
    api.get<{ chains: { id: string; name: string; symbol: string; tier: string; added: boolean }[] }>(
      `/wallets/${id}/chains`,
    ),

  addresses: (accountId: number, change?: 0 | 1) =>
    api.get<{ addresses: AddressRecord[] }>(
      `/accounts/${accountId}/addresses${change === undefined ? '' : `?change=${change}`}`,
    ),
  receiveAddress: (accountId: number) =>
    api.get<{ address: AddressRecord; uri: string }>(`/accounts/${accountId}/receive`),
  utxos: (accountId: number) => api.get<{ utxos: Utxo[] }>(`/accounts/${accountId}/utxos`),
  freezeUtxo: (id: number, frozen: boolean) => api.post<{ ok: boolean }>(`/utxos/${id}/freeze`, { frozen }),

  sync: (chainId?: string) => api.post<SyncResult>('/sync', chainId ? { chainId } : {}),
  syncStatus: () => api.get<{ running: boolean }>('/sync/status'),

  portfolio: (currency?: string) =>
    api.get<Portfolio>(`/portfolio${currency ? `?currency=${currency}` : ''}`),
  portfolioHistory: (days: number) =>
    api.get<{ history: { day: number; value: string }[] }>(`/portfolio/history?days=${days}`),

  prices: (ids: string[], currency = 'usd') =>
    api.get<{ prices: Record<string, { price: string; change24h: number | null; stale: boolean }> }>(
      `/prices?ids=${ids.join(',')}&currency=${currency}`,
    ),
  priceChart: (id: string, days: number) =>
    api.get<{ prices: [number, number][] }>(`/prices/${id}/chart?days=${days}`),

  estimateFee: (chainId: string) => api.post<FeeEstimate>('/tx/estimate-fee', { chainId }),
  buildTx: (body: {
    accountId: number;
    outputs: { address: string; amount: string; max?: boolean }[];
    feeRate?: number;
    token?: string | null;
    selectedUtxoIds?: number[];
    rbf?: boolean;
  }) => api.post<BuiltTx>('/tx/build', body),
  signTx: (body: { accountId: number; payload: string; format: 'psbt' | 'rlp'; paths?: string[] }) =>
    api.post<{ signed: { txid: string; hex: string; fee: string } }>('/tx/sign', body),
  broadcastTx: (chainId: string, hex: string) =>
    api.post<{ txid: string }>('/tx/broadcast', { chainId, hex }),
  txHistory: (chainId?: string) =>
    api.get<{
      transfers: (Record<string, unknown> & { fiat: string | null })[];
      currency: string;
    }>(`/tx/history${chainId ? `?chainId=${chainId}` : ''}`),

  validateAddress: (address: string, chainId?: string) =>
    api.post<AddressValidation>('/validate-address', { address, chainId }),

  // --- payment requests ----------------------------------------------------

  createRequest: (body: {
    accountId: number;
    mode: 'single' | 'multi';
    title: string;
    note?: string;
    chainId: string;
    amount: string;
    expiryDays?: number;
    webhookUrl?: string;
    webhookSecret?: string;
  }) =>
    api
      .post<{ request: Record<string, unknown> }>('/requests', body)
      .then((r) => ({ request: mapPaymentRequest(r.request) })),

  requests: () =>
    api
      .get<{ requests: (Record<string, unknown> & { status: RequestStatus })[] }>('/requests')
      .then((r) => ({
        requests: r.requests.map((row) => ({
          ...mapPaymentRequest(row),
          status: row.status,
        })),
      })),

  requestDetail: (id: number) =>
    api
      .get<{ request: Record<string, unknown>; status: RequestStatus }>(`/requests/${id}`)
      .then((r) => ({ request: mapPaymentRequest(r.request), status: r.status })),

  cancelRequest: (id: number) =>
    api.patch<{ ok: true }>(`/requests/${id}`, { action: 'cancel' }),
  deleteRequest: (id: number) => api.delete<{ ok: true }>(`/requests/${id}`),

  publicRequest: (slug: string) =>
    api.get<PublicRequest>(`/public/requests/${slug}`),

  // --- personal API tokens ---------------------------------------------------

  createToken: (label: string) =>
    api.post<{ token: string; row: ApiTokenRow }>('/tokens', { label }),
  tokens: () => api.get<{ tokens: ApiTokenRow[] }>('/tokens'),
  revokeToken: (id: number) => api.delete<{ ok: true }>(`/tokens/${id}`),
  parseUri: (uri: string) =>
    api.post<{
      chainId?: string;
      address: string;
      amount?: string;
      amountFormatted: string | null;
      label?: string;
    }>('/parse-uri', { uri }),

  validateMnemonic: (mnemonic: string) =>
    api.post<{ valid: boolean; wordCount: number; problems: string[] }>('/mnemonic/validate', {
      mnemonic,
    }),
  generateMnemonic: (words: number) =>
    api.post<{ mnemonic: string }>('/mnemonic/generate', { words }),
  suggestWords: (prefix: string) =>
    api.get<{ words: string[] }>(`/mnemonic/suggest?prefix=${encodeURIComponent(prefix)}`),

  contacts: () => api.get<{ contacts: Record<string, unknown>[] }>('/contacts'),
  addContact: (body: { name: string; address: string; chainId?: string; notes?: string }) =>
    api.post<{ contact: unknown }>('/contacts', body),
  deleteContact: (id: number) => api.del<{ ok: boolean }>(`/contacts/${id}`),

  settings: () => api.get<{ settings: Record<string, string> }>('/settings'),
  saveSettings: (settings: Record<string, string>) => api.put<{ ok: boolean }>('/settings', settings),

  endpoints: () => api.get<{ endpoints: Record<string, unknown>[] }>('/endpoints'),
  addEndpoint: (body: { chainId: string; url: string; kind: string }) =>
    api.post<{ ok: boolean }>('/endpoints', body),

  totpBegin: () => api.post<{ secret: string; uri: string }>('/auth/totp/begin'),
  totpConfirm: (code: string) => api.post<{ ok: boolean }>('/auth/totp/confirm', { code }),

  // -- household and profiles ---------------------------------------------

  household: () =>
    api.get<{
      household: { id: number; name: string; currency: string };
      profiles: Profile[];
      activeProfileId: number;
      overview: HouseholdOverview | null;
    }>("/household"),

  createProfile: (body: {
    name: string;
    role: ProfileRole;
    birthYear?: number;
    pin?: string;
    avatar?: string;
    color?: string;
  }) => api.post<{ profile: Profile }>("/profiles", body),

  updateProfile: (id: number, body: Record<string, unknown>) =>
    api.patch<{ profile: Profile }>(`/profiles/${id}`, body),

  archiveProfile: (id: number) => api.del<{ ok: boolean }>(`/profiles/${id}`),
  setProfilePin: (id: number, pin: string | null) =>
    api.post<{ ok: boolean }>(`/profiles/${id}/pin`, { pin }),
  unlockProfile: (id: number) => api.post<{ ok: boolean }>(`/profiles/${id}/unlock`),
  switchProfile: (id: number, pin?: string) =>
    api.post<{ profile: Profile }>(`/profiles/${id}/switch`, pin ? { pin } : {}),

  approvals: () =>
    api.get<{ pending: ApprovalRequest[]; mine: ApprovalRequest[] }>("/approvals"),
  decideApproval: (id: number, approve: boolean, reason?: string) =>
    api.post<{ status: string }>(`/approvals/${id}/decide`, { approve, reason }),

  allowances: () => api.get<{ allowances: Allowance[] }>("/allowances"),
  createAllowance: (body: {
    profileId: number;
    amountCents: string;
    frequency: "daily" | "weekly" | "biweekly" | "monthly";
    autoSend?: boolean;
  }) => api.post<{ id: number }>("/allowances", body),
  deleteAllowance: (id: number) => api.del<{ ok: boolean }>(`/allowances/${id}`),

  goals: (profileId?: number) =>
    api.get<{ goals: SavingsGoal[] }>(`/goals${profileId ? `?profileId=${profileId}` : ""}`),
  createGoal: (body: { name: string; targetCents: string; emoji?: string; profileId?: number }) =>
    api.post<{ id: number }>("/goals", body),
  contributeToGoal: (id: number, amountCents: string) =>
    api.post<{ saved: string; achieved: boolean }>(`/goals/${id}/contribute`, { amountCents }),
  deleteGoal: (id: number) => api.del<{ ok: boolean }>(`/goals/${id}`),

  // -- cards ----------------------------------------------------------------

  cards: () =>
    api.get<{ cards: CardRecord[]; categoryGroups: string[]; notice: string }>("/cards"),
  issueCard: (body: {
    profileId: number;
    nickname: string;
    perTxLimitCents?: string;
    dailyLimitCents?: string;
    blockedCategories?: string[];
    roundUp?: boolean;
  }) => api.post<{ card: CardRecord }>("/cards", body),
  freezeCard: (id: number, frozen: boolean) =>
    api.post<{ card: CardRecord }>(`/cards/${id}/freeze`, { frozen }),
  updateCard: (id: number, body: Record<string, unknown>) =>
    api.patch<{ card: CardRecord }>(`/cards/${id}`, body),
  cardTransactions: (id: number) =>
    api.get<{ transactions: CardTransaction[]; summary: CardSummary }>(`/cards/${id}/transactions`),
  simulateAuthorization: (
    id: number,
    body: { merchantName: string; amountCents: string; mcc?: string; currency?: string },
  ) =>
    api.post<{
      approved: boolean;
      reason?: string;
      authorizationId: number;
      roundUpCents?: string;
      decidedInMs: number;
    }>(`/cards/${id}/authorize`, body),

  // -- spend ----------------------------------------------------------------

  spendProviders: () =>
    api.get<{
      providers: { id: string; name: string; credentialFree: boolean; products: string[] }[];
      notice: string;
    }>("/spend/providers"),
  startSpend: (body: {
    productType: "gift_card" | "esim" | "topup";
    currency?: string;
    preferredChain?: string;
    theme?: "light" | "dark";
    slug?: string;
    country?: string;
  }) =>
    api.post<
      | {
          status: "ready";
          orderId: number;
          embed: { url: string; expectedOrigin: string; instructions: string };
        }
      | { status: "awaiting_approval"; orderId: number; approvalId: number; reason: string }
    >("/spend/start", body),
  spendEvent: (orderId: number, event: Record<string, unknown>) =>
    api.post<{ status: string }>(`/spend/${orderId}/event`, event),
  spendOrders: () => api.get<{ orders: SpendOrderRecord[] }>("/spend/orders"),

  changePassword: (oldPassword: string, newPassword: string) =>
    api.post<{ ok: true }>("/auth/password", { oldPassword, newPassword }),
  currencies: () => api.get<{ currencies: string[] }>("/currencies"),
  search: (q: string) =>
    api.get<{ results: { kind: string; ref: string; body: string }[] }>(
      `/search?q=${encodeURIComponent(q)}`,
    ),
  taxUnrealised: () =>
    api.get<{
      positions: {
        symbol: string;
        chain_id: string;
        amount: string;
        costBasis: string;
        value: string;
        gain: string;
        gainPercent: number;
      }[];
    }>("/tax/unrealised"),
  alerts: () => api.get<{ alerts: AlertRecord[] }>("/alerts"),
  createAlert: (body: {
    type: AlertRecord["type"];
    chainId?: string;
    assetId?: number;
    threshold?: string;
    note?: string;
  }) => api.post<{ alert: AlertRecord }>("/alerts", body),
  deleteAlert: (id: number) => api.del<{ ok: true }>(`/alerts/${id}`),
  knownAddresses: () =>
    api.get<{ addresses: KnownAddress[] }>("/addresses/known"),
  rememberAddress: (body: { chainId: string; address: string; label?: string }) =>
    api.post<{ ok: true }>("/addresses/remember", body),
  verifyAddress: (body: { chainId: string; address: string }) =>
    api.post<{ ok: true }>("/addresses/verify", body),
  releaseTimeLock: (id: number) =>
    api.post<{ ok: true }>(`/timelocks/${id}/release`, {}),
  deleteTimeLock: (id: number) => api.del<{ ok: true }>(`/timelocks/${id}`),
  addLabel: (body: { kind: string; ref: string; label: string; origin?: string }) =>
    api.post<{ ok: true }>("/labels", body),
  allowancesDue: () =>
    api.get<{ due: { id: number; profile_name: string; amount_cents: number }[] }>(
      "/allowances/due",
    ),
  snapshotPortfolio: () => api.post<{ ok: true }>("/portfolio/snapshot", {}),


  pending: () =>
    api.get<{
      transfers: Record<string, unknown>[];
      unconfirmed: Record<string, unknown>[];
      inFlight: boolean;
    }>("/pending"),

  testing: () =>
    api.get<{
      chains: {
        chainId: string;
        name: string;
        symbol: string;
        summary: string;
        recommended: boolean;
        faucets: { name: string; url: string; note?: string }[];
      }[];
      samples: { label: string; chainId: string; address: string; note: string }[];
      guidance: { title: string; body: string; steps: string[] }[];
      lastVerified: string;
    }>("/testing"),

  privacyReport: () =>
    api.get<{
      score: number;
      grade: "excellent" | "good" | "fair" | "poor";
      findings: {
        id: string;
        severity: "high" | "medium" | "low";
        title: string;
        detail: string;
        action?: string;
        count: number;
      }[];
      checkedAddresses: number;
      checkedUtxos: number;
    }>("/privacy"),
  freezeDust: () => api.post<{ frozen: number }>("/privacy/freeze-dust", {}),

  auditLog: () => api.get<{ entries: AuditEntry[] }>("/auth/audit"),
  sessions: () =>
    api.get<{ sessions: SessionRecord[]; current: string }>("/auth/sessions"),
  exportLabels: () => api.get<string>("/labels/export"),
  createWatchAddresses: (body: { name: string; chainId: string; addresses: string[] }) =>
    api.post<{ wallet: Wallet }>("/wallets/watch-addresses", body),
  previewWatchWallet: (body: { chainId: string; xpub: string; addressType?: string }) =>
    api.post<{
      source: "xpub" | "slip132" | "descriptor";
      addressType: string | null;
      fingerprint: string | null;
      originPath: string | null;
      addresses: string[];
    }>("/wallets/watch/preview", body),
  createWatchWallet: (body: {
    name: string;
    chainId: string;
    xpub: string;
    addressType?: string;
  }) => api.post<{ wallet: Wallet }>("/wallets/watch", body),

  onrampOptions: () =>
    api.get<{
      hosted: OnrampOption[];
      noKyc: OnrampOption[];
      swaps: OnrampOption[];
      notice: string;
      faq: { question: string; answer: string }[];
    }>("/onramp/options"),

  onrampUrl: (body: { accountId: number; fiat?: string; amount?: string; country?: string }) =>
    api.post<{ url: string; address: string; chainId: string }>("/onramp/url", body),

  spendCatalog: (params: { country?: string; q?: string; category?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params.country) qs.set("country", params.country);
    if (params.q) qs.set("q", params.q);
    if (params.category && params.category !== "all") qs.set("category", params.category);
    if (params.limit) qs.set("limit", String(params.limit));
    return api.get<CatalogPage>(`/spend/catalog?${qs.toString()}`);
  },

  spendProduct: (slug: string) => api.get<ProductDetail>(`/spend/product/${encodeURIComponent(slug)}`),

  // -- safety ---------------------------------------------------------------

  checkAddress: (chainId: string, address: string) =>
    api.post<{
      risk: "none" | "new" | "lookalike" | "known_good";
      message: string;
      resembles?: string;
      timesUsed?: number;
      verified?: boolean;
    }>("/addresses/check", { chainId, address }),

  inheritance: () =>
    api.get<{ plans: InheritancePlan[]; explainer: string }>("/inheritance"),
  createInheritance: (body: {
    walletId: number;
    heirName: string;
    heirContact?: string;
    envelopeBase64: string;
    envelopeHint?: string;
    checkInDays?: number;
    graceDays?: number;
  }) => api.post<{ plan: InheritancePlan }>("/inheritance", body),
  inheritanceCheckIn: (id: number) =>
    api.post<{ status: string; nextDueAt: number }>(`/inheritance/${id}/check-in`),
  deleteInheritance: (id: number) => api.del<{ ok: boolean }>(`/inheritance/${id}`),

  timeLocks: () => api.get<{ locks: TimeLock[] }>("/timelocks"),
  createTimeLock: (body: { name: string; chainId: string; unlockAt: number; kind?: "soft" | "cltv" }) =>
    api.post<{ id: number }>("/timelocks", body),

  duressStatus: () => api.get<{ configured: boolean; triggeredAt: number | null }>("/duress"),
  configureDuress: (pin: string, decoyProfileId: number | null) =>
    api.post<{ ok: boolean; advice: string }>("/duress", { pin, decoyProfileId }),

  taxReport: (year: number) =>
    api.get<{ shortTerm: string; longTerm: string; total: string; disposals: unknown[] }>(
      `/tax/report?year=${year}`,
    ),
};
